import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Step, StepLabel, Stepper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { v4 } from 'uuid';
import _ from 'lodash';
import xlsx from 'xlsx';
import Grid from '@mui/system/Unstable_Grid';
import { DropzoneArea } from 'mui-file-dropzone';
const steps = ['Carregamento de ficheiro', 'Validação de dados', 'Importação'];
/**
 * Descrição dos steps:
 * 1 - Carregamento de ficheiro
       Carregamento do ficheiro Excel/CSV
 * 2 - Validação de dados
      Leitura do ficheiro e validação dos dados, apresentando os erros encontrados (com método validador externo, que pode ser assíncrono)
 * 3 - Importação
      Envio de dados para o servidor, linha a linha, que insere na base de dados (spinner), criar método remoto
 * 4 - Conclusão
      Apresentação de resumo da importação, com erros e dados importados
 *
 */
const allowed = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
export const ImportDialog = ({ onClose, fields, importer, validator, ...props }) => {
    const indexFields = fields.filter(f => f.index);
    if (indexFields.length !== 1) {
        throw new Error('É necessário definir exatamente um campo com index: true');
    }
    const indexField = fields.find(f => f.index);
    const [step, setStep] = useState(0);
    const [data, setData] = useState([]);
    const [result, setResult] = useState([]);
    const [total, setTotal] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const [file, setFile] = useState(null);
    const [majorError, setMajorError] = useState('');
    const formik = useFormik({
        initialValues: {
            file: null,
            clienteId: null
        },
        validateOnMount: false,
        validationSchema: yup.object({
            file: yup.mixed().required('O campo é obrigatório')
        }),
        onSubmit: async (d) => {
            if (file) {
                setStep(1);
                const reader = new FileReader();
                reader.onload = async (e) => {
                    const fileData = e.target?.result;
                    if (fileData) {
                        let wb = xlsx.read(fileData, {
                            type: 'binary',
                            cellDates: true
                        });
                        const wsname = wb.SheetNames[0];
                        const ws = wb.Sheets[wsname];
                        if (wb.SheetNames.length > 0) {
                            const rows = xlsx.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1 });
                            if (rows.length == 0) {
                                setMajorError('O ficheiro carregado não contém linhas na primeira folha. Valide o ficheiro o tente novamente.');
                                return;
                            }
                            // Remove linhas vazias
                            _.remove(rows, (c) => c.length == 0);
                            // Remove header
                            let header = rows.shift();
                            // Valida se tem todos os campos obrigatórios
                            const missing = fields
                                .filter(f => f.required)
                                .filter(f => !header.includes(f.label))
                                .map(f => f.label);
                            if (missing.length > 0) {
                                setMajorError('O ficheiro carregado não contém as seguintes colunas obrigatórias: ' + missing.join(', '));
                                return;
                            }
                            setTotal(rows.length);
                            // Para cada linha, cria um objeto com a estrutura definida, chamando o validador para cada linha
                            for await (let row of rows) {
                                let obj = {
                                    valid: true,
                                    data: {}
                                };
                                let errosCampo = [];
                                // Criar objeto com os campos
                                fields.forEach(f => {
                                    const colIndex = header.indexOf(f.label);
                                    obj.data[f.key] = colIndex !== -1 ? row[colIndex] ?? null : null;
                                });
                                // Verifica campos obrigatórios
                                fields
                                    .filter(f => f.required)
                                    .forEach(f => {
                                    if (obj.data[f.key] === null || obj.data[f.key] === undefined || obj.data[f.key] === '') {
                                        errosCampo.push(f.label);
                                    }
                                });
                                if (errosCampo.length > 0) {
                                    obj.valid = false;
                                    obj.error = `Faltam os seguintes campos obrigatórios: ${errosCampo.join(', ')}`;
                                }
                                if (!obj.valid) {
                                    setData(old => [...old, obj]);
                                    continue;
                                }
                                else {
                                    const r = await validator(obj);
                                    setData(old => [...old, r]);
                                }
                            }
                        }
                        else {
                            setMajorError('O ficheiro carregado não contém folhas. Valide o ficheiro o tente novamente.');
                        }
                    }
                };
                reader.readAsBinaryString(file);
            }
        }
    });
    const startImport = async () => {
        setStep(2);
        const done = [];
        const toImport = data.filter(d => d.valid);
        for await (let item of toImport) {
            let success = false;
            try {
                const r = await importer(item.data);
                const index = toImport.indexOf(item);
                if (index % 5 === 0) {
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
                success = true;
            }
            catch (error) {
                success = false;
            }
            done.push({
                data: item.data,
                success: success
            });
            setResult([...done]);
        }
        setResult([...done]);
    };
    const renderActions = () => {
        switch (step) {
            case 0:
                return (<Button disabled={file == null || !formik.isValid} onClick={() => {
                        formik.submitForm();
                    }} variant='contained'>
            OK
          </Button>);
            case 1:
                return (<Button disabled={data.length == 0 || data.length != total} onClick={() => {
                        startImport();
                    }}>
            INICIAR
          </Button>);
            case 2:
                return (<Button disabled={data.filter(r => r.valid).length != result.length} onClick={() => {
                        onClose(true);
                    }}>
            FECHAR
          </Button>);
        }
    };
    const renderSteps = () => {
        switch (step) {
            case 0:
                return [
                    <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid lg={12} xs={12}>
              <DropzoneArea filesLimit={1} dropzoneText='Arraste um ficheiro para esta janela ou clique para adicionar' onChange={files => {
                            if (files.length > 0) {
                                let t = files.pop();
                                let f = new File([t], `${v4()}.${t.extension()}`, { type: t.type });
                                f.dummy = t.dummy || false;
                                formik.setFieldValue('file', f.name);
                                setFile(f);
                            }
                            else {
                                formik.setFieldValue('file', undefined);
                                setFile(null);
                            }
                        }} onDelete={_ => {
                            formik.setFieldValue('file', undefined);
                            setFile(null);
                        }} acceptedFiles={allowed} showFileNames={false} showAlerts={false} showPreviewsInDropzone={true} useChipsForPreview={true} previewChipProps={{
                            color: 'default',
                            variant: 'filled'
                        }}/>
            </Grid>
          </Grid>
                ];
            case 1:
                return [
                    <Grid container spacing={2} sx={{ mt: 1 }}>
            {majorError.length > 0 ? (<Grid lg={12} xs={12} sx={{ mt: 1 }}>
                <Alert severity='error'>{majorError}</Alert> : <></>
                <Typography>
                  Clique em <b>CANCELAR</b> para fechar o importador.
                </Typography>
              </Grid>) : (<></>)}
            {data.length > 1 ? (<Grid lg={12} xs={12} sx={{ mt: 1 }}>
                {data.length > 0 && data.length == total && data.filter(r => !r.valid).length == 0 ? (<Alert severity='success'>
                    Prima <b>INICIAR</b> para começar a importação.
                  </Alert>) : (<></>)}
                {data.length > 0 && data.length == total && data.filter(r => !r.valid).length > 0 ? (<Alert severity='warning'>
                    Existem linhas com erros de validação. Prima <b>INICIAR</b> para começar a importação de {data.filter(r => r.valid).length} linha(s) válida(s), ou <b>CANCELAR</b> para fechar o importador.
                  </Alert>) : (<></>)}
                <Stack direction='row' spacing={1} sx={{ mt: 1, justifyContent: 'space-between' }}>
                  <Typography>{data.length != total ? `A validar linha: ${data.length} / ${total}` : `Todas as linhas validadas (${total})`}</Typography>
                  <Typography align='right'>
                    Todas as linhas{' '}
                    <Switch onChange={() => {
                                setShowAll(!showAll);
                            }} checked={!showAll} size='small'/>{' '}
                    Apenas válidas
                  </Typography>
                </Stack>

                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>{indexField.label}</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(showAll ? data : data.filter(r => r.valid)).map((l, i) => (<TableRow key={i} hover>
                          <TableCell>{l.data[indexField.key]}</TableCell>
                          <TableCell>{l.valid ? 'Linha válida para importação' : l.error}</TableCell>
                        </TableRow>))}
                      {!showAll && data.filter(r => !r.valid).length == 0 ? (<TableRow>
                          <TableCell colSpan={2} align='center'>
                            Não existem erros de validação!
                          </TableCell>
                        </TableRow>) : (<></>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>) : (<></>)}
          </Grid>
                ];
            case 2:
                return [
                    <Grid container spacing={2} sx={{ mt: 1 }}>
            {majorError.length > 0 ? (<Grid lg={12} xs={12} sx={{ mt: 1 }}>
                <Alert severity='error'>{majorError}</Alert> : <></>
                <Typography>
                  Clique em <b>CANCELAR</b> para fechar o importador.
                </Typography>
              </Grid>) : (<></>)}
            {result.length > 0 ? (<Grid lg={12} xs={12} sx={{ mt: 1 }}>
                {result.length > 0 && result.length == total && result.filter(r => !r.success).length == 0 ? (<Alert severity='success'>
                    Prima <b>AVANÇAR</b> para concluir a importação.
                  </Alert>) : (<></>)}
                {result.length > 0 && result.length == total && result.filter(r => !r.success).length > 0 ? (<Alert severity='warning'>
                    A importação acabou com falhas. Prima <b>AVANÇAR</b> para concluir a importação.
                  </Alert>) : (<></>)}
                <Stack direction='row' spacing={1} sx={{ mt: 1, justifyContent: 'space-between' }}>
                  <Typography>
                    A importar linha: {result.length} / {data.filter(r => r.valid).length}
                  </Typography>
                  <Typography align='right'>
                    Todas as linhas{' '}
                    <Switch onChange={() => {
                                setShowAll(!showAll);
                            }} checked={!showAll} size='small'/>{' '}
                    Apenas erros
                  </Typography>
                </Stack>

                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>{indexField.label}</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(showAll ? result : result.filter(r => !r.success)).map((l, i) => (<TableRow key={i} hover>
                          <TableCell>{l.data[indexField.key]}</TableCell>
                          <TableCell>{l.success ? 'Linha importada com sucesso' : 'Importação falhou'}</TableCell>
                        </TableRow>))}
                      {!showAll && result.filter(r => !r.success).length == 0 ? (<TableRow>
                          <TableCell colSpan={2} align='center'>
                            Não existem erros de importação!
                          </TableCell>
                        </TableRow>) : (<></>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>) : (<></>)}
          </Grid>
                ];
        }
    };
    return (<Dialog {...props} fullWidth maxWidth={'lg'}>
      <DialogTitle>
        {props.title || 'Importação de dados'}
        <Stepper sx={{ mt: 1 }} activeStep={step} alternativeLabel>
          {steps.map(label => (<Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>))}
        </Stepper>
        <Divider sx={{ mt: 1 }}/>
      </DialogTitle>
      <DialogContent sx={{
            height: '600px',
            mt: 1
        }}>
        {renderSteps()}
      </DialogContent>
      <DialogActions>
        <Button type='button' onClick={() => onClose(false)}>
          Cancelar
        </Button>
        {renderActions()}
      </DialogActions>
    </Dialog>);
};
